import { useMemo } from "react";
import { getSessionTimestamp } from "../utils/dateUtils";
import { shouldIncludeDurationInAverage } from "../utils/sessionValidation";

interface Session {
  id: number;
  score?: number | null;
  score_max?: number | null;
  total_session_duration?: number | null;
  total_session_completion_percentage?: number | null;
  is_completed?: boolean;
  created_at_ts?: number | null;
  created_at?: string | null;
  createdAt?: string | null;
}

interface ModuleStats {
  totalSessions: number;
  completedSessions: number;
  abandonedSessions: number;
  avgScore: number | null;
  avgMaxScore: number | null;
  avgDuration: number | null;
  completionRate: number;
  avgProgressionPercent: number | null;
  bestScore: number | null;
  worstScore: number | null;
  lastScore: number | null;
}

/**
 * Custom hook to compute module statistics from a list of sessions.
 * Extracted from ModuleView to improve maintainability and reusability.
 */
export const useModuleStats = (sessions: Session[]): ModuleStats => {
  return useMemo(() => {
    const totalSessions = sessions.length;
    const completedSessions = sessions.filter((s) => s.is_completed).length;
    const abandonedSessions = totalSessions - completedSessions;

    // Only include sessions with valid scores (not null/undefined and >= 0, since -1 means no score)
    const sessionsWithScores = sessions.filter((s) => s.score != null && s.score >= 0);
    const scores = sessionsWithScores.map((s) => s.score as number);
    const maxScores = sessionsWithScores
      .filter((s) => s.score_max != null)
      .map((s) => s.score_max as number);

    const avgScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
    const avgMaxScore =
      maxScores.length > 0 ? maxScores.reduce((a, b) => a + b, 0) / maxScores.length : null;

    // Only include sessions with valid durations (excluding old sessions < 30s)
    const sessionsWithDurations = sessions.filter((s) => {
      if (s.total_session_duration == null) return false;
      const createdAt = s.createdAt || s.created_at || s.created_at_ts;
      if (!createdAt) return false;
      return shouldIncludeDurationInAverage(createdAt, s.total_session_duration);
    });
    const durations = sessionsWithDurations.map((s) => s.total_session_duration as number);
    const avgDuration =
      durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : null;

    const completionRate = totalSessions > 0 ? (completedSessions / totalSessions) * 100 : 0;

    // Calculate average progression percentage
    const sessionsWithProgression = sessions.filter(
      (s) => s.total_session_completion_percentage != null,
    );
    const progressions = sessionsWithProgression.map(
      (s) => s.total_session_completion_percentage as number,
    );
    const avgProgressionPercent =
      progressions.length > 0
        ? progressions.reduce((a, b) => a + b, 0) / progressions.length
        : null;

    // Find best, worst, and last scores (only from sessions with valid scores)
    const sortedByScore = [...sessionsWithScores].sort(
      (a, b) => (b.score as number) - (a.score as number),
    );
    const bestScore = sortedByScore.length > 0 ? (sortedByScore[0].score as number) : null;
    const worstScore =
      sortedByScore.length > 0 ? (sortedByScore[sortedByScore.length - 1].score as number) : null;

    const sortedByDate = [...sessions].sort(
      (a, b) => getSessionTimestamp(b) - getSessionTimestamp(a),
    );
    const lastScore = sortedByDate[0]?.score ?? null;

    return {
      totalSessions,
      completedSessions,
      abandonedSessions,
      avgScore,
      avgMaxScore,
      avgDuration,
      completionRate,
      avgProgressionPercent,
      bestScore,
      worstScore,
      lastScore,
    };
  }, [sessions]);
};

export default useModuleStats;
